import { ParecerSection, ParecerItem, ParsedParecer } from "./types";

/**
 * Checks if a line is a section separator (contains only dashes, underscores, or spaces)
 */
const isSeparatorLine = (line: string): boolean => {
  const trimmed = line.trim();
  if (trimmed.length === 0) return false;
  // Check if line contains only dashes, underscores, spaces, or a combination
  return /^[\s\-_]+$/.test(trimmed);
};

/**
 * Checks if a line looks like a section title (all caps, no colons, short)
 */
const isSectionTitle = (line: string): boolean => {
  const trimmed = line.trim();
  if (trimmed.length === 0) return false;
  // Section titles are usually short, all caps, and don't contain colons
  return (
    trimmed.length < 50 &&
    trimmed === trimmed.toUpperCase() &&
    !trimmed.includes(":") &&
    trimmed.split(" ").length <= 5
  );
};

/**
 * Parses a key-value pair from a line (format: "KEY: VALUE")
 */
const parseKeyValue = (line: string): ParecerItem | null => {
  const colonIndex = line.indexOf(":");
  if (colonIndex === -1) return null;

  const label = line.substring(0, colonIndex).trim();
  const value = line.substring(colonIndex + 1).trim();

  if (!label || !value) return null;

  return { label, value };
};

/**
 * Parses the parecer text into structured sections
 */
export const parseParecer = (parecerText: string): ParsedParecer => {
  if (!parecerText || typeof parecerText !== "string") {
    return { sections: [], rawText: parecerText || "" };
  }

  // Normalize line breaks: replace \r\n, \r, \n with \n
  const normalizedText = parecerText
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");

  const lines = normalizedText.split("\n");
  const sections: ParecerSection[] = [];
  let currentSection: ParecerSection | null = null;
  let currentItems: ParecerItem[] = [];
  let pendingTitle: string | null = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Skip empty lines
    if (line.length === 0) {
      continue;
    }

    // Check if it's a separator line
    if (isSeparatorLine(line)) {
      // If we have a pending title, use it for the next section
      if (pendingTitle) {
        // Save current section if it has items
        if (currentItems.length > 0) {
          if (currentSection) {
            currentSection.items = currentItems;
            sections.push(currentSection);
          } else {
            sections.push({ items: currentItems });
          }
        }
        currentSection = { title: pendingTitle, items: [] };
        currentItems = [];
        pendingTitle = null;
      } else {
        // Save current section if it has items
        if (currentItems.length > 0) {
          if (currentSection) {
            currentSection.items = currentItems;
            sections.push(currentSection);
          } else {
            sections.push({ items: currentItems });
          }
          currentItems = [];
          currentSection = null;
        }
      }
      continue;
    }

    // Check if it's a section title (appears before separator or at start)
    if (isSectionTitle(line)) {
      // If we already have items, save them first
      if (currentItems.length > 0) {
        if (currentSection) {
          currentSection.items = currentItems;
          sections.push(currentSection);
        } else {
          sections.push({ items: currentItems });
        }
        currentItems = [];
      }
      // Store as pending title (will be used when we hit a separator or start adding items)
      pendingTitle = line;
      currentSection = { title: line, items: [] };
      continue;
    }

    // Try to parse as key-value pair
    const keyValue = parseKeyValue(line);
    if (keyValue) {
      // If we have a pending title but no section yet, create it
      if (pendingTitle && !currentSection) {
        currentSection = { title: pendingTitle, items: [] };
        pendingTitle = null;
      }
      
      // Check if this is a repeated key (like multiple CEP:, FONE:, etc.)
      // Group them together in the same item
      const existingItemIndex = currentItems.findIndex(
        (item) => item.label === keyValue.label
      );
      
      if (existingItemIndex >= 0) {
        // Append to existing item with line break
        currentItems[existingItemIndex].value += `\n${keyValue.value}`;
      } else {
        currentItems.push(keyValue);
      }
    } else if (line.length > 0) {
      // If it's not a key-value pair but has content
      // Check if it's a continuation of the last value
      if (currentItems.length > 0) {
        const lastItem = currentItems[currentItems.length - 1];
        
        // If last item is part of a list (like addresses or phones), append with line break
        if (
          lastItem.label &&
          (lastItem.label.includes("CEP") ||
            lastItem.label.includes("ENDEREÇO") ||
            lastItem.label.includes("BAIRRO") ||
            lastItem.label.includes("CIDADE") ||
            lastItem.label.includes("ESTADO") ||
            lastItem.label.includes("FONE") ||
            lastItem.label.includes("TELEFONE"))
        ) {
          // Check if this line is a new entry (starts with same key pattern)
          const nextLine = i < lines.length - 1 ? lines[i + 1]?.trim() : "";
          if (
            nextLine &&
            (nextLine.startsWith("CEP:") ||
              nextLine.startsWith("ENDEREÇO:") ||
              nextLine.startsWith("FONE:") ||
              nextLine.startsWith("TELEFONE:"))
          ) {
            // This is a continuation of current entry, append with space
            lastItem.value += ` ${line}`;
          } else {
            // This might be a new entry, append with line break
            lastItem.value += `\n${line}`;
          }
        } else {
          // Regular continuation
          lastItem.value += ` ${line}`;
        }
      } else {
        // Create a new item with empty label (might be a title or header)
        if (pendingTitle && !currentSection) {
          currentSection = { title: pendingTitle, items: [] };
          pendingTitle = null;
        }
        currentItems.push({ label: "", value: line });
      }
    }
  }

  // Save the last section
  if (currentItems.length > 0) {
    if (currentSection) {
      currentSection.items = currentItems;
      sections.push(currentSection);
    } else {
      sections.push({ items: currentItems });
    }
  }

  return {
    sections: sections.filter((section) => section.items.length > 0),
    rawText: parecerText
  };
};
